import { createContext } from 'react';

import { getRoundState } from '../components/_organisms/LiveGame';
import { Slide } from '../components/SlideComponents/slide.types';
import { DEFAULT_AUDIO_VOLUME } from '../hooks/useAudio';
import { Game } from '../services/backendService/types';
import { getRoundCategory } from './roundUtils';

export type MusicState = {
  song?: string;
  isLooping: boolean;
  autoplay: boolean;
  volume?: number;
};

export const GameMusicContext = createContext<{
  music: MusicState;
  setMusic: React.Dispatch<React.SetStateAction<MusicState>>;
}>({
  music: {
    isLooping: true,
    autoplay: true,
    song: undefined,
    volume: DEFAULT_AUDIO_VOLUME,
  },
  setMusic: () => undefined,
});
export const musicMapper = {
  lobby: {
    song: '/sum_of_funk.wav',
    loop: true,
  },
  ask: {
    song: '/questions_blazer_rail.wav',
    loop: true,
  },
  idle: {
    song: '/end_of_questions.wav',
    loop: false,
  },
  vote: {
    song: '/voting_waiting_game.wav',
    loop: true,
  },
  leaderboard: {
    song: '/leaderboard.wav',
    loop: false,
  },
};

// Sound configuration type for themed blocks
type BlockSoundConfig = {
  lobby: { song: string; loop: boolean };
  ask: { song: string; loop: boolean };
};

// Maps block names to their sound configurations
// Only blocks with custom sounds need to be listed here
const blockSoundMapper: Record<string, BlockSoundConfig> = {
  themedPracticeAnimalsActivity: {
    lobby: { song: '/themes/animals/lobby.wav', loop: true },
    ask: { song: '/themes/animals/questions.wav', loop: true },
  },
  themedPracticeWizardActivity: {
    lobby: { song: '/themes/wizard/lobby.wav', loop: true },
    ask: { song: '/themes/wizard/questions.wav', loop: true },
  },
  themedPracticeChristmasActivity: {
    lobby: { song: '/themes/christmas/lobby.wav', loop: true },
    ask: { song: '/themes/christmas/questions.wav', loop: true },
  },
  themedPracticeSpaceActivity: {
    lobby: { song: '/themes/space/lobby.wav', loop: true },
    ask: { song: '/themes/space/questions.wav', loop: true },
  },
  themedPracticeHimalayasActivity: {
    lobby: { song: '/themes/himalayas/lobby.wav', loop: true },
    ask: { song: '/themes/himalayas/questions.wav', loop: true },
  },
  // Add more themed blocks as they are implemented:
  // themedPracticeChristmasActivity: {
  //   lobby: { song: '/themes/christmas/lobby.wav', loop: true },
  //   ask: { song: '/themes/christmas/questions.wav', loop: true },
  // },
};

// Get sound config for a game based on its slides' block labels
const getBlockSoundConfig = (
  slides: Slide[] | null | undefined,
): BlockSoundConfig | undefined => {
  if (!slides) return undefined;

  for (const slide of slides) {
    if (slide.blockLabel && blockSoundMapper[slide.blockLabel]) {
      return blockSoundMapper[slide.blockLabel];
    }
  }
  return undefined;
};

export const findRoundSong = (game: Game, now: () => number, showLobby: boolean) => {
  const slideState = getRoundState({ game, now });

  const slide = game.slides?.find(slide => slide.id === game.currentRound);

  // Check if this game uses custom block sounds (e.g., themed practice)
  const blockSounds = getBlockSoundConfig(game.slides);
  const hasCustomSounds = !!blockSounds;

  // Lobby music
  if (showLobby && slideState !== 'in' && slideState !== 'countdown') {
    return blockSounds?.lobby || musicMapper.lobby;
  }

  if (!showLobby && slideState === 'pre') return null;
  // For blocks with custom sounds, don't play idle/transition music
  if (!showLobby && slideState === 'post-between') {
    return hasCustomSounds ? null : musicMapper.idle;
  }

  const isInSlideCountdown =
    game.isPlaying &&
    slideState === 'in' &&
    game.roundStartsAt &&
    slide?.durationSeconds &&
    new Date(game.roundStartsAt + slide?.durationSeconds * 1000).getTime() > now();

  const slideCategory = getRoundCategory(slide?.type);

  // Question/response phase
  if (isInSlideCountdown && slideCategory === 'response') {
    return blockSounds?.ask || musicMapper.ask;
  }

  const isInPreRoundCountdown =
    game.isPlaying &&
    slideState === 'countdown' &&
    game.roundStartsAt &&
    game.roundStartsAt > now();

  if (isInPreRoundCountdown && slideCategory === 'response') {
    return blockSounds?.ask || musicMapper.ask;
  }

  // For blocks with custom sounds, don't play vote music
  if (isInPreRoundCountdown && slideCategory === 'collaboration') {
    return hasCustomSounds ? null : musicMapper.vote;
  }

  // For blocks with custom sounds, don't play leaderboard music
  if (game.isPlaying && slideCategory === 'results' && !slide?.wasPlayed) {
    return hasCustomSounds ? null : musicMapper.leaderboard;
  }

  if (game.isPlaying && slideState === 'pre') {
    // prevent playing music when moving from lobby straight to activity
    return null;
  }
  if (game.isPlaying && slideCategory === 'results' && slide?.wasPlayed) {
    // prevent playing music when the round has been played
    return null;
  }

  if (game.isPlaying && slideState === 'post' && !game.roundStartsAt) {
    // prevent playing music when navigating between slides
    return null;
  }
  // For blocks with custom sounds, don't play idle music on post state
  if (game.isPlaying && slideState === 'post') {
    return hasCustomSounds ? null : musicMapper.idle;
  }

  // keep playing
  return undefined;
};

export const calculateVolume = (vol: number) => {
  const cubicVolume = Math.pow(vol, 3);

  return cubicVolume;
};
